import { Alerts } from "../../../utils/Alerts.js";
import { RPCs } from "../../../services/rpc.js";
import { useRPCContext } from "../../../contexts/RPCContext.js";
import { useRPC } from "../../../hooks/useRPC/index.js";
import { FormikTextarea } from "../../../components/FormikFields/FormikTextarea.js";
import { FormikSelect } from "../../../components/FormikFields/FormikSelect.js";
import { REFS_LINKS_BY_TYPE_SELECT_OPTIONS, REFS_LINK_TYPE_LABELS_MAP } from "../RefsByTypeTitles/constants.js";
import { SelectRefLinkValueForType } from "./components/SelectRefLinkValueForType.js";
import { useEffect } from "react";
import { Button, FormLayout, Icon, useModalContext } from "@therms/atalaya";
import { IoLink } from "react-icons/io5";
import { IoMdAdd } from "react-icons/io";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import { MdDeleteForever } from "react-icons/md";
import { FieldArray, Formik } from "formik";
import * as Yup from "yup";
//#region src/modules/shared/RefLinkForm/index.tsx
var validationSchema = Yup.object({
	controller: Yup.object().shape({
		type: Yup.string().required(),
		value: Yup.string().required()
	}),
	id: Yup.string().optional(),
	reason: Yup.string(),
	refs: Yup.array().min(1).when("id", {
		is: (id) => typeof id === "string",
		otherwise: (schema) => schema.min(1),
		then: (schema) => schema.min(2, "At least 2 Refs must exist")
	}).of(Yup.object().shape({
		type: Yup.string().required("Must be selected"),
		value: Yup.string().required("Must be selected")
	}))
});
function RefLinkForm({ controller, id, onComplete }) {
	const modalCtx = useModalContext();
	const { rpcService } = useRPCContext();
	const { data, isCalling } = useRPC(RPCs.RefLinks.GetRefLink.v1, id ? { id } : void 0, { fire: !!id });
	useEffect(() => {
		if (modalCtx) modalCtx.setHeader?.("Ref Link");
	}, []);
	const handleDelete = () => {
		if (id) rpcService.RefLinks.DeleteRefLink.v1.call({ refLinkId: id }).then(() => {
			Alerts.success("Ref Link deleted");
			onComplete?.();
		}).catch((err) => {
			Alerts.error("Error deleting Ref Link", err.message);
		});
	};
	const handleSave = (values) => {
		if (!data?.controller && !controller) {
			Alerts.error("Error saving Ref Link", "Controller must exist");
			return;
		}
		return rpcService.RefLinks.SaveRefLink.v1.call({
			controller: data?.controller || controller,
			id,
			reason: values.reason,
			refs: values.refs
		}).then(() => {
			Alerts.success("Ref Link saved");
			onComplete?.();
		}).catch((err) => {
			Alerts.error("Error saving Ref Link", err.message);
		});
	};
	return /* @__PURE__ */ jsx(Formik, {
		enableReinitialize: true,
		initialValues: data || {
			controller,
			refs: []
		},
		onSubmit: handleSave,
		validationSchema,
		children: ({ errors, handleSubmit, submitCount, values }) => {
			return /* @__PURE__ */ jsxs(FormLayout, {
				horizontal: false,
				loading: isCalling,
				heading: !modalCtx ? "Ref Link" : void 0,
				children: [
					/* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsxs("div", {
						className: "space-x-xs flex",
						children: [/* @__PURE__ */ jsx(Icon, {
							color: "subtle",
							Icon: IoLink
						}), /* @__PURE__ */ jsx("div", { children: REFS_LINK_TYPE_LABELS_MAP[controller.type].single })]
					}), /* @__PURE__ */ jsx(FormLayout.Section, {
						space: "xxs",
						subTitle: /* @__PURE__ */ jsxs(Fragment$1, { children: [errors["refs"] && submitCount && !!values.refs && values.refs?.length < 2 && !!values.id && /* @__PURE__ */ jsx("span", {
							className: "text-critical",
							children: "At least 2 Refs must exist"
						}), errors["refs"] && submitCount && !values.id && !values.refs?.length && /* @__PURE__ */ jsx("span", {
							className: "text-critical",
							children: "At least 1 Ref must exist"
						})] }),
						children: /* @__PURE__ */ jsx(FieldArray, {
							name: "refs",
							render: (arrayHelpers) => /* @__PURE__ */ jsxs(Fragment$1, { children: [values.refs?.map((ref, i) => /* @__PURE__ */ jsxs("div", {
								className: "space-x-xs flex items-center",
								children: [/* @__PURE__ */ jsxs("div", {
									className: "text-color-subtle -mt-6 flex flex-col items-center justify-center",
									children: ["|", /* @__PURE__ */ jsx(Icon, {
										color: ref.value === values.controller?.value || !!values.controller?.redirectRef && ref.value === values.controller?.redirectRef?.value ? "info" : "subtle",
										Icon: IoLink
									})]
								}), /* @__PURE__ */ jsxs("div", {
									className: "space-x-xxs flex w-full",
									children: [
										/* @__PURE__ */ jsx(FormikSelect, {
											options: REFS_LINKS_BY_TYPE_SELECT_OPTIONS,
											name: `refs.${i}.type`,
											placeholder: "Type"
										}, `refs.${i}.type`),
										/* @__PURE__ */ jsxs("div", {
											className: "flex-1",
											children: [/* @__PURE__ */ jsx(SelectRefLinkValueForType, {
												onChangeValue: (value) => {
													arrayHelpers.replace(i, {
														...ref,
														value
													});
												},
												type: ref.type,
												value: ref.value
											}, `refs.${i}.value`), !!ref.type && !ref.value && errors["refs"] && !!submitCount && /* @__PURE__ */ jsx("span", {
												className: "text-critical text-xs",
												children: "Must be selected"
											})]
										}),
										/* @__PURE__ */ jsx(Button, {
											disabled: ref.value === values.controller?.value || !!values.controller?.redirectRef && ref.value === values.controller?.redirectRef?.value,
											onClick: () => arrayHelpers.remove(i),
											size: "sm",
											subtle: true,
											variant: "critical",
											children: /* @__PURE__ */ jsx(Icon, { Icon: MdDeleteForever })
										})
									]
								})]
							}, i)), /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsxs(Button, {
								onClick: () => arrayHelpers.push({}),
								size: "sm",
								subtle: true,
								variant: "main",
								children: [/* @__PURE__ */ jsx(Icon, {
									Icon: IoMdAdd,
									size: "sm"
								}), " Add Ref"]
							}) })] })
						})
					})] }),
					/* @__PURE__ */ jsx(FormikTextarea, {
						label: "Reason for Ref Link",
						name: "reason",
						placeholder: "Reason..."
					}),
					/* @__PURE__ */ jsx(FormLayout.Controls, {
						onCancel: onComplete,
						onDelete: handleDelete,
						onSave: handleSubmit,
						submitLabel: "Save"
					})
				]
			});
		}
	});
}
//#endregion
export { RefLinkForm };

//# sourceMappingURL=index.js.map